import materialsQueries from '@/api/modules/materialsQueries'
import { notificationService } from '@/services/notificationService'
import { useMaterialsStore } from '@/stores'
import { handleApiError } from '@/utils/apiHelpers'
import { defaultTranslate, normalizePaginationParams } from '@/utils/utils'
import { buildFiltersParams } from '@/views/Configurations/Materials/helpers/utils'

class PapersService {
    constructor({ store = useMaterialsStore(), t = defaultTranslate } = {}) {
        this.store = store
        this.t = t
    }

    async fetchPapers({ filters, pagination } = {}) {
        const { store, t } = this

        store.setPapersLoading(true)
        const params = {
            ...normalizePaginationParams(pagination),
            ...buildFiltersParams(filters)
        }

        try {
            const response = await materialsQueries.fetchPapers(params)
            const { data } = response

            store.setPapers(data.results)
            store.setPapersMeta({
                count: data.count,
                next: data.next,
                previous: data.previous,
                current: data.current
            })

            return response
        } catch (error) {
            handleApiError(error, t('configurations.materials.pages.papers.errors.fetchPapers'))
            throw error
        } finally {
            store.setPapersLoading(false)
        }
    }

    async createPaper(payload) {
        const { t } = this

        try {
            const response = await materialsQueries.createPaper(payload)
            notificationService.success(t('configurations.materials.pages.papers.notifications.paperSaved'))
            return response
        } catch (error) {
            handleApiError(error, t('configurations.materials.pages.papers.errors.savePaper'))
            throw error
        }
    }

    async updatePaper(paperId, payload) {
        const { t } = this

        try {
            const response = await materialsQueries.updatePaper(paperId, payload)
            notificationService.success(t('configurations.materials.pages.papers.notifications.paperSaved'))
            return response
        } catch (error) {
            handleApiError(error, t('configurations.materials.pages.papers.errors.savePaper'))
            throw error
        }
    }

    async fetchCoatingTypes() {
        const { store, t } = this

        try {
            const response = await materialsQueries.fetchCoatingTypes()
            store.setCoatingTypes(response.data.results)
            return response
        } catch (error) {
            handleApiError(error, t('configurations.materials.pages.papers.errors.fetchCoatingTypes'))
            throw error
        }
    }

    async fetchPrinergyPaperTypes() {
        const { store, t } = this

        try {
            const response = await materialsQueries.fetchPrinergyPaperTypes()
            store.setPrinergyPaperTypes(response.data.results)
            return response
        } catch (error) {
            handleApiError(error, t('configurations.materials.pages.papers.errors.fetchPrinergyPaperTypes'))
            throw error
        }
    }

    setPapersFilters(filters) {
        this.store.setPapersFilters(filters)
    }

    resetPapersFilters() {
        this.store.resetPapersFilters()
    }

    async generatePapersPriceList({ userEmail } = {}) {
        const { t } = this

        try {
            const response = await materialsQueries.generatePapersPriceList()
            if (userEmail) {
                notificationService.success(
                    t('configurations.materials.pages.papers.notifications.priceListGenerated', {
                        email: userEmail
                    })
                )
            } else {
                notificationService.success(t('configurations.materials.pages.papers.notifications.priceListGeneratedNoEmail'))
            }
            return response
        } catch (error) {
            handleApiError(error, t('configurations.materials.pages.papers.errors.generatePapersPriceList'))
            throw error
        }
    }

    async importPapersPriceList(formData) {
        const { store, t } = this

        if (!formData) return null

        try {
            const response = await materialsQueries.importPapersPriceList(formData)

            notificationService.success(t('configurations.materials.common.notifications.fileUploaded'))

            return response
        } catch (error) {
            handleApiError(error, t('configurations.materials.common.errors.importPriceList'))
            throw error
        } finally {
            store.resetImportState()
        }
    }
}

export const usePapersService = (options) => new PapersService(options)
